// Webclient → Chat. SMS conversations for the logged-in user's department,
// backed by the P1–P3 SMS API. Live via the WebSocket (sms.inbound/sms.outbound).
// Left: conversation list. Right: thread + composer.
// UI: Google-Messages-style avatar + relative timestamps + date separators + emoji picker.

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Smile, Paperclip, Gift, X as XIcon, Send } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { onEvent } from '../lib/ws';
import { relativeTime, shortTime, dayLabel, isoDay } from '../lib/chatTime';
import { useActiveTenant } from '../lib/tenantScope';

type Conv = { conversation_uuid: string; peer_number: string; our_number: string; last_preview: string; last_message_at: string; unread: number };
type Media = { url: string; content_type: string };
type Msg  = { message_uuid: string; direction: 'inbound' | 'outbound'; from_number: string; to_number: string; body: string; status: string; error_code: string | null; created_at: string; media?: Media[] };

// ── Avatar ───────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
  'bg-violet-500', 'bg-blue-500', 'bg-sky-500',    'bg-teal-500',
  'bg-green-500',  'bg-amber-500', 'bg-orange-500', 'bg-rose-500',
];

function phoneAvatarColor(phone: string): string {
  let h = 0;
  for (const c of phone) h = ((h * 31) + c.charCodeAt(0)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function PhoneAvatar({ phone, size = 'md' }: { phone: string; size?: 'sm' | 'md' }) {
  const digits = phone.replace(/\D/g, '');
  const label  = digits.slice(-4, -2) || digits.slice(-2) || '?';
  const color  = phoneAvatarColor(phone);
  const cls    = size === 'sm' ? 'h-8 w-8 text-[10px]' : 'h-9 w-9 text-[11px]';
  return (
    <span className={`shrink-0 inline-flex items-center justify-center rounded-full font-bold text-white ${color} ${cls}`}>
      {label}
    </span>
  );
}

// ── Emoji picker ─────────────────────────────────────────────────────────────

const EMOJIS = [
  '😀','😂','😊','😍','🥺','😎','😢','😡',
  '👍','👎','👏','🙏','❤️','🔥','✅','❌',
  '🎉','✨','💯','💪','👀','🤔','😴','🤣',
  '😅','😬','🥳','🎊','💬','📱','📞','⏰',
];

function EmojiPicker({ onPick, onClose }: { onPick: (e: string) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const tid = setTimeout(() => document.addEventListener('mousedown', onDoc), 50);
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(tid); document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  return (
    <div ref={ref} className="absolute bottom-full left-0 mb-2 z-50 rounded-xl border border-border bg-elevated shadow-2xl w-72">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-textSecondary">Emoji</span>
        <button type="button" onClick={onClose} className="text-textSecondary hover:text-textPrimary">
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-8 gap-1 p-2">
        {EMOJIS.map(e => (
          <button key={e} type="button" onClick={() => { onPick(e); onClose(); }}
            className="rounded p-1 text-xl hover:bg-elevated/80 transition-colors">
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

// GIF picker (#543) — GIPHY via the server-side /api/sms/gif-search proxy.
// A picked GIF's media.giphy.com URL goes straight into the attachment chips
// (Twilio fetches it as MMS media — no upload round-trip).
type GifResult = { id: string; title: string; url: string; preview_url: string };

function GifPicker({ onPick, onClose }: { onPick: (g: GifResult) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<GifResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const tid = setTimeout(() => document.addEventListener('mousedown', onDoc), 50);
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(tid); document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // Debounced search; empty query = trending.
  useEffect(() => {
    const tid = setTimeout(async () => {
      setLoading(true); setError('');
      try {
        const r = await fetch(`/api/sms/gif-search?q=${encodeURIComponent(q.trim())}&limit=24`,
          { headers: { Authorization: `Bearer ${getToken()}` } });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || 'GIF search failed');
        setResults(j.results || []);
      } catch (e: any) { setError(e.message); setResults([]); }
      finally { setLoading(false); }
    }, q ? 350 : 0);
    return () => clearTimeout(tid);
  }, [q]);

  return (
    <div ref={ref} className="absolute bottom-full left-0 mb-2 z-50 rounded-xl border border-border bg-elevated shadow-2xl w-80">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search GIFs…"
          className="input py-1 text-sm flex-1" />
        <button type="button" onClick={onClose} className="text-textSecondary hover:text-textPrimary">
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1 p-2 max-h-72 overflow-y-auto">
        {loading && <div className="col-span-2 text-center text-xs text-textSecondary py-6">Searching…</div>}
        {!loading && error && <div className="col-span-2 text-center text-xs text-red-400 py-6">{error}</div>}
        {!loading && !error && results.length === 0 && <div className="col-span-2 text-center text-xs text-textSecondary py-6">No GIFs found.</div>}
        {!loading && results.map(g => (
          <button key={g.id} type="button" title={g.title} onClick={() => { onPick(g); onClose(); }}
            className="rounded-lg overflow-hidden hover:ring-2 hover:ring-primary transition">
            <img src={g.preview_url} alt={g.title} className="w-full h-24 object-cover" loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── SMS pane ─────────────────────────────────────────────────────────────────

export function SmsPane() {
  const qc = useQueryClient();
  const [active, setActive]       = useState<string | null>(null);
  const [draft, setDraft]         = useState('');
  const [newTo, setNewTo]         = useState('');
  const [composing, setComposing] = useState(false);
  const [err, setErr]             = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // MMS attachments (#543): uploaded to /api/sms/media, sent as media_urls.
  const [showGifs, setShowGifs] = useState(false);
  const [attachments, setAttachments] = useState<Media[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function uploadFiles(files: FileList | null) {
    if (!files || !files.length) return;
    setUploading(true); setErr('');
    try {
      for (const f of Array.from(files).slice(0, 10 - attachments.length)) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await fetch('/api/sms/media', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || 'upload failed');
        setAttachments(prev => [...prev, { url: j.url, content_type: j.content_type }]);
      }
    } catch (e: any) {
      setErr(e.message || 'upload failed');
      setTimeout(() => setErr(''), 4000);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }
  const threadEnd   = useRef<HTMLDivElement | null>(null);

  const tenant = useActiveTenant();
  const cfg   = useQuery({ queryKey: ['sms-config', tenant], queryFn: () => api.sms.config() });
  const convs = useQuery({ queryKey: ['sms-convs', tenant], queryFn: () => api.sms.conversations(), refetchInterval: 30_000 });
  const msgs  = useQuery({
    queryKey: ['sms-msgs', tenant, active],
    queryFn:  () => api.sms.messages(active as string),
    enabled:  !!active,
  });

  // Live updates
  useEffect(() => {
    const refresh = () => {
      qc.invalidateQueries({ queryKey: ['sms-convs'] });
      if (active) qc.invalidateQueries({ queryKey: ['sms-msgs'] });
    };
    const offIn  = onEvent('sms.inbound',  refresh);
    const offOut = onEvent('sms.outbound', refresh);
    return () => { offIn(); offOut(); };
  }, [qc, active]);

  // Client switched in the header: the open thread belongs to the OLD
  // department — drop it (and any half-typed compose) so the pane rebuilds
  // cleanly from the new client's conversations.
  useEffect(() => {
    const onTenant = () => { setActive(null); setComposing(false); setErr(''); setAttachments([]); };
    window.addEventListener('stratus-tenant-changed', onTenant);
    return () => window.removeEventListener('stratus-tenant-changed', onTenant);
  }, []);

  // Auto-scroll to newest message
  useEffect(() => { threadEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.data, active]);

  // Clear unread badge on open
  useEffect(() => {
    if (!active) return;
    const c = (convs.data || []).find((x: Conv) => x.conversation_uuid === active);
    if (c && c.unread > 0) api.sms.markRead(active).then(() => qc.invalidateQueries({ queryKey: ['sms-convs'] })).catch(() => {});
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 40), 160)}px`;
  }, [draft]);

  const send = useMutation({
    mutationFn: ({ to, body, media_urls }: { to: string; body: string; media_urls?: string[] }) => api.sms.send(to, body, media_urls),
    onSuccess: (r) => {
      setErr(''); setDraft(''); setNewTo(''); setComposing(false); setAttachments([]);
      setActive(r.conversation_uuid);
      qc.invalidateQueries({ queryKey: ['sms-convs'] });
      qc.invalidateQueries({ queryKey: ['sms-msgs'] });
    },
    onError: (e: any) => setErr(e.message || 'send failed'),
  });

  const activeConv = (convs.data || []).find((c: Conv) => c.conversation_uuid === active);
  const peer       = composing ? newTo : activeConv?.peer_number || '';

  const submit = () => {
    const body = draft.trim();
    const to   = (composing ? newTo : activeConv?.peer_number || '').trim();
    if (!to || (!body && !attachments.length)) return;
    send.mutate({ to, body, media_urls: attachments.length ? attachments.map(a => a.url) : undefined });
  };

  // Not configured
  if (cfg.data && !cfg.data.enabled) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center">
        <div className="max-w-md">
          <div className="text-lg font-semibold mb-2">Texting isn't turned on yet</div>
          <div className="text-textSecondary text-sm">
            An administrator can enable SMS under
            <span className="text-textPrimary"> System → SMS</span>, then set the texting number.
          </div>
        </div>
      </div>
    );
  }

  // Build message list with day-boundary separators
  const msgList: Array<{ type: 'separator'; label: string; key: string } | { type: 'msg'; data: Msg }> = [];
  let lastDay = '';
  for (const m of (msgs.data || []) as Msg[]) {
    const day = isoDay(m.created_at);
    if (day !== lastDay) {
      msgList.push({ type: 'separator', label: dayLabel(m.created_at), key: `sep-${day}` });
      lastDay = day;
    }
    msgList.push({ type: 'msg', data: m });
  }

  return (
    <div className="h-full flex min-h-0">

      {/* ── Conversation list ── */}
      <div className="w-[300px] shrink-0 border-r border-border flex flex-col min-h-0">
        <div className="h-12 shrink-0 flex items-center justify-between px-3 border-b border-border">
          <div className="leading-tight min-w-0">
            <span className="font-semibold text-sm">Messages</span>
            {/* whose number these messages send from — with the client switcher in
                the header, an admin needs this to never text as the wrong business */}
            {cfg.data?.from_number && (
              <div className="text-[10px] text-textSecondary truncate">texting as {cfg.data.from_number}</div>
            )}
          </div>
          <button onClick={() => { setComposing(true); setActive(null); setErr(''); }}
            className="text-primary text-sm hover:underline">New</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {(convs.data || []).length === 0 && !composing && (
            <div className="p-4 text-sm text-textSecondary">No conversations yet.</div>
          )}
          {(convs.data || []).map((c: Conv) => {
            const isActive = active === c.conversation_uuid;
            return (
              <button key={c.conversation_uuid}
                onClick={() => { setActive(c.conversation_uuid); setComposing(false); }}
                className={`w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-elevated flex items-center gap-2.5 ${isActive ? 'bg-elevated' : ''}`}>
                <PhoneAvatar phone={c.peer_number} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1.5">
                    <span className={`text-sm truncate ${c.unread > 0 ? 'font-semibold' : 'font-medium'} text-textPrimary`}>
                      {c.peer_number}
                    </span>
                    <span className="text-[10px] text-textSecondary shrink-0">
                      {c.last_message_at ? relativeTime(c.last_message_at) : ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-1 mt-0.5">
                    <span className={`text-xs truncate ${c.unread > 0 ? 'text-textPrimary font-medium' : 'text-textSecondary'}`}>
                      {c.last_preview}
                    </span>
                    {c.unread > 0 && (
                      <span className="shrink-0 text-[10px] bg-primary text-white rounded-full px-1.5 py-0.5 leading-tight">
                        {c.unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Thread ── */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {!active && !composing ? (
          <div className="flex-1 flex items-center justify-center text-textSecondary text-sm">
            Select a conversation or start a new one.
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="h-12 shrink-0 flex items-center px-4 border-b border-border gap-3">
              {composing ? (
                <input autoFocus value={newTo} onChange={e => setNewTo(e.target.value)}
                  placeholder="Recipient number, e.g. (989) 555-1212"
                  className="input py-1 text-sm flex-1" />
              ) : (
                <>
                  <PhoneAvatar phone={peer} size="sm" />
                  <div>
                    <div className="font-semibold text-sm">{peer}</div>
                    {activeConv?.our_number && (
                      <div className="text-[10px] text-textSecondary">via {activeConv.our_number}</div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
              {msgs.isLoading && active && (
                <div className="text-textSecondary text-sm text-center py-6">Loading…</div>
              )}

              {msgList.map((item, i) => {
                if (item.type === 'separator') {
                  return (
                    <div key={item.key} className="flex items-center gap-3 py-3">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-[10px] font-medium text-textSecondary uppercase tracking-wider whitespace-nowrap">
                        {item.label}
                      </span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  );
                }

                const m   = item.data;
                const isOut = m.direction === 'outbound';

                return (
                  <div key={m.message_uuid} className={`flex items-end gap-2 ${isOut ? 'flex-row-reverse' : ''}`}>
                    {!isOut && <PhoneAvatar phone={m.from_number} size="sm" />}
                    <div className={`max-w-[70%] ${isOut ? 'items-end' : 'items-start'} flex flex-col`}>
                      <div className={`rounded-2xl px-3 py-2 text-sm ${
                        isOut
                          ? 'bg-primary text-white rounded-br-sm'
                          : 'bg-elevated text-textPrimary rounded-bl-sm'
                      }`}>
                        {(m.media || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(m.media || []).map((md, mi) => (
                              <a key={mi} href={md.url} target="_blank" rel="noreferrer">
                                <img src={md.url} alt="attachment"
                                  className="max-h-48 max-w-full rounded-lg object-cover"
                                  loading="lazy" />
                              </a>
                            ))}
                          </div>
                        )}
                        {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                      </div>
                      <div className={`text-[10px] mt-0.5 text-textSecondary ${isOut ? 'text-right' : ''}`}>
                        {shortTime(m.created_at)}{m.status === 'failed' ? ' · failed' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={threadEnd} />
            </div>

            {/* Error */}
            {err && <div className="px-4 py-2 text-xs text-red-400 border-t border-border">{err}</div>}

            {/* Composer */}
            <div className="shrink-0 border-t border-border bg-surface p-3">
              {/* Attachment chips */}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 pb-2">
                  {attachments.map((a, i) => (
                    <div key={i} className="relative">
                      <img src={a.url} alt="attachment" className="h-16 w-16 rounded-lg object-cover border border-border" />
                      <button type="button" title="Remove"
                        onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                        className="absolute -top-1.5 -right-1.5 bg-surface border border-border rounded-full p-0.5 text-textSecondary hover:text-textPrimary">
                        <XIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {uploading && <div className="h-16 w-16 rounded-lg border border-border flex items-center justify-center text-xs text-textSecondary">…</div>}
                </div>
              )}
              <div className="relative flex items-end gap-2">
                {/* GIF picker popover */}
                {showGifs && (
                  <GifPicker
                    onPick={g => setAttachments(prev => prev.length < 10 ? [...prev, { url: g.url, content_type: 'image/gif' }] : prev)}
                    onClose={() => setShowGifs(false)}
                  />
                )}

                {/* Emoji picker popover */}
                {showEmoji && (
                  <EmojiPicker
                    onPick={e => {
                      const el = textareaRef.current;
                      if (!el) { setDraft(d => d + e); return; }
                      const start = el.selectionStart ?? draft.length;
                      const end   = el.selectionEnd   ?? draft.length;
                      const next  = draft.slice(0, start) + e + draft.slice(end);
                      setDraft(next);
                      setTimeout(() => { el.focus(); el.setSelectionRange(start + e.length, start + e.length); }, 0);
                    }}
                    onClose={() => setShowEmoji(false)}
                  />
                )}

                {/* Toolbar */}
                <div className="flex items-center gap-0.5 pb-1">
                  <button type="button" title="Emoji"
                    onClick={() => setShowEmoji(v => !v)}
                    className={`p-1.5 rounded-lg transition-colors ${showEmoji ? 'bg-primary/20 text-primary' : 'text-textSecondary hover:text-textPrimary hover:bg-elevated'}`}>
                    <Smile className="h-5 w-5" />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple hidden
                    onChange={e => uploadFiles(e.target.files)} />
                  <button type="button" title="Attach image"
                    disabled={uploading || attachments.length >= 10}
                    onClick={() => fileInputRef.current?.click()}
                    className={`p-1.5 rounded-lg transition-colors ${uploading ? 'text-primary' : 'text-textSecondary hover:text-textPrimary hover:bg-elevated'} disabled:opacity-50`}>
                    <Paperclip className="h-5 w-5" />
                  </button>
                  <button type="button" title="Send GIF"
                    disabled={attachments.length >= 10}
                    onClick={() => { setShowGifs(v => !v); setShowEmoji(false); }}
                    className={`p-1.5 rounded-lg transition-colors ${showGifs ? 'bg-primary/20 text-primary' : 'text-textSecondary hover:text-textPrimary hover:bg-elevated'} disabled:opacity-50`}>
                    <Gift className="h-5 w-5" />
                  </button>
                </div>

                {/* Text input */}
                <textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
                  rows={1}
                  placeholder="Text message…"
                  className="input flex-1 resize-none text-sm py-2"
                  style={{ minHeight: 40, maxHeight: 160 }}
                />

                {/* Send */}
                <button onClick={submit}
                  disabled={send.isPending || uploading || (!draft.trim() && !attachments.length) || !peer.trim()}
                  className="btn-primary p-2.5 rounded-full disabled:opacity-50 shrink-0"
                  title="Send">
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Team pane — internal extension-to-extension chat ─────────────────────────
// Backed by api/routes/mobile_chat_desktop.js (mounted at /api/mobile).
// Wire contract: GET /chat/threads -> { threads: [{extension_uuid,name,last_message,unread}] }
//                GET /chat/messages/:extUuid -> { messages: [{id,direction,message,created_at}] }
//                POST /chat/send -> { to_extension_uuid, message }

type TeamThread = {
  extension_uuid: string | null;
  name: string;
  last_message: string;
  unread: number;
};

type TeamMsg = {
  id: string;
  direction: 'sent' | 'received';
  message: string;
  created_at: string;
};

// Initials avatar reusing the same palette as PhoneAvatar but keyed on name.
function nameAvatarColor(name: string): string {
  let h = 0;
  for (const c of name) h = ((h * 31) + c.charCodeAt(0)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function NameAvatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const parts  = name.trim().split(/\s+/);
  const label  = parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase() || '?';
  const color  = nameAvatarColor(name);
  const cls    = size === 'sm' ? 'h-8 w-8 text-[10px]' : 'h-9 w-9 text-[11px]';
  return (
    <span className={`shrink-0 inline-flex items-center justify-center rounded-full font-bold text-white ${color} ${cls}`}>
      {label}
    </span>
  );
}

function TeamPane() {
  const qc = useQueryClient();
  const tenant = useActiveTenant();
  const [active, setActive] = useState<TeamThread | null>(null);
  const [draft, setDraft]   = useState('');
  const [err, setErr]       = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const threadEnd   = useRef<HTMLDivElement | null>(null);

  // Thread list — poll every 10 s.
  const threads = useQuery<{ threads: TeamThread[] }>({
    queryKey: ['team-threads', tenant],
    queryFn: () => fetch('/api/mobile/chat/threads', {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(r => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))),
    refetchInterval: 10_000,
    select: d => d,
  });

  // Message history for the open thread — poll every 10 s.
  // GET also auto-marks incoming messages as read server-side.
  const msgs = useQuery<{ messages: TeamMsg[] }>({
    queryKey: ['team-msgs', tenant, active?.extension_uuid],
    queryFn: () => fetch(`/api/mobile/chat/messages/${active!.extension_uuid}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(r => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))),
    enabled: !!(active?.extension_uuid),
    refetchInterval: 10_000,
  });

  // Refetch thread list when a thread is opened (unread count drops).
  useEffect(() => {
    if (active) qc.invalidateQueries({ queryKey: ['team-threads'] });
  }, [active?.extension_uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to newest.
  useEffect(() => { threadEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.data, active]);

  // Auto-resize textarea.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 40), 160)}px`;
  }, [draft]);

  const send = useMutation({
    mutationFn: ({ to_extension_uuid, message }: { to_extension_uuid: string; message: string }) =>
      fetch('/api/mobile/chat/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ to_extension_uuid, message }),
      }).then(async r => {
        if (!r.ok) { const t = await r.text(); throw new Error(t || `${r.status}`); }
        return r.json();
      }),
    onSuccess: () => {
      setDraft(''); setErr('');
      qc.invalidateQueries({ queryKey: ['team-msgs', active?.extension_uuid] });
      qc.invalidateQueries({ queryKey: ['team-threads'] });
    },
    onError: (e: any) => setErr(e.message || 'send failed'),
  });

  const submit = () => {
    const body = draft.trim();
    if (!body || !active?.extension_uuid) return;
    send.mutate({ to_extension_uuid: active.extension_uuid, message: body });
  };

  const threadList: TeamThread[] = threads.data?.threads ?? [];

  return (
    <div className="h-full flex min-h-0">

      {/* Left: thread list */}
      <div className="w-[300px] shrink-0 border-r border-border flex flex-col min-h-0">
        <div className="h-12 shrink-0 flex items-center px-3 border-b border-border">
          <span className="font-semibold text-sm">Team</span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {threads.isLoading && (
            <div className="p-4 text-sm text-textSecondary">Loading…</div>
          )}
          {!threads.isLoading && threadList.length === 0 && (
            <div className="p-4 text-sm text-textSecondary">No conversations yet.</div>
          )}
          {threadList.map(t => {
            const isActive = active?.extension_uuid === t.extension_uuid;
            return (
              <button
                key={t.extension_uuid ?? t.name}
                onClick={() => { setActive(t); setErr(''); }}
                className={`w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-elevated flex items-center gap-2.5 ${isActive ? 'bg-elevated' : ''}`}
              >
                <NameAvatar name={t.name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1.5">
                    <span className={`text-sm truncate ${t.unread > 0 ? 'font-semibold' : 'font-medium'} text-textPrimary`}>
                      {t.name}
                    </span>
                    {t.unread > 0 && (
                      <span className="shrink-0 text-[10px] bg-primary text-white rounded-full px-1.5 py-0.5 leading-tight">
                        {t.unread}
                      </span>
                    )}
                  </div>
                  <div className={`text-xs truncate mt-0.5 ${t.unread > 0 ? 'text-textPrimary font-medium' : 'text-textSecondary'}`}>
                    {t.last_message || ' '}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: thread + composer */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-textSecondary text-sm">
            Select a conversation or ask a teammate to message you first.
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="h-12 shrink-0 flex items-center px-4 border-b border-border gap-3">
              <NameAvatar name={active.name} size="sm" />
              <span className="font-semibold text-sm">{active.name}</span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
              {msgs.isLoading && (
                <div className="text-textSecondary text-sm text-center py-6">Loading…</div>
              )}
              {(msgs.data?.messages ?? []).map(m => {
                const isSent = m.direction === 'sent';
                return (
                  <div key={m.id} className={`flex items-end gap-2 ${isSent ? 'flex-row-reverse' : ''}`}>
                    {!isSent && <NameAvatar name={active.name} size="sm" />}
                    <div className={`max-w-[70%] flex flex-col ${isSent ? 'items-end' : 'items-start'}`}>
                      <div className={`rounded-2xl px-3 py-2 text-sm ${
                        isSent
                          ? 'bg-primary text-white rounded-br-sm'
                          : 'bg-elevated text-textPrimary rounded-bl-sm'
                      }`}>
                        <span className="whitespace-pre-wrap break-words">{m.message}</span>
                      </div>
                      <div className={`text-[10px] mt-0.5 text-textSecondary ${isSent ? 'text-right' : ''}`}>
                        {shortTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={threadEnd} />
            </div>

            {/* Error banner */}
            {err && <div className="px-4 py-2 text-xs text-red-400 border-t border-border">{err}</div>}

            {/* Composer */}
            <div className="shrink-0 border-t border-border bg-surface p-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
                  rows={1}
                  placeholder={`Message ${active.name}…`}
                  className="input flex-1 resize-none text-sm py-2"
                  style={{ minHeight: 40, maxHeight: 160 }}
                />
                <button
                  onClick={submit}
                  disabled={send.isPending || !draft.trim()}
                  className="btn-primary p-2.5 rounded-full disabled:opacity-50 shrink-0"
                  title="Send"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function Chat() {
  const [mode, setMode] = useState<'sms' | 'team'>('sms');
  const Tab = ({ id, label }: { id: 'sms' | 'team'; label: string }) => (
    <button onClick={() => setMode(id)}
      className={`px-4 py-2 text-sm border-b-2 -mb-px ${
        mode === id ? 'border-primary text-primary font-medium' : 'border-transparent text-textSecondary hover:text-textPrimary'
      }`}>
      {label}
    </button>
  );
  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="shrink-0 flex items-center gap-1 px-3 border-b border-border">
        <Tab id="sms" label="SMS" />
        <Tab id="team" label="Team" />
      </div>
      <div className="flex-1 min-h-0">
        {mode === 'sms' ? <SmsPane /> : <TeamPane />}
      </div>
    </div>
  );
}
